import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { ArtikelZulage } from '../../../../../shared/types/artikelZulage';
import { getArtikelZulagen, ArtikelZulageSelectors } from '../../../../../store/ArtikelZulage.store';
import { createArtikelZulageFilter, useArtikelZulagePartnerContext } from '../../../../hooks/useArtikelZulagePartnerContext';
import { selectArtikelLoseWareList } from '../../../../../store/ArtikelLoseWare.store';
import Select from 'react-select';
import { createCustomStyles } from '../../../../ui-components/SelectInput/CustomStyles';
import { PropertyEntryProps } from '../types';
import { BestellpositionArtikelLoseWareView } from '../../../../types';
import styles from './ZulageMobileDropdown.module.scss';
import TrashIcon from '../../../../../assets/icon-trashcan.svg';
import AddIcon from '../../../../../assets/icon-add.svg';
import Config from '../../../../../shared/config';

interface ZulageOption {
    value: string;
    label: string;
    zulage: ArtikelZulage;
}

interface ZulageEntry {
    zulageId?: string;
    dosierwert?: number;
    freitext?: string;
    zulageText?: string;
    zulage?: boolean;
}

export const ZulageMobileDropdown = ({
    bestellposition,
    disabled,
}: PropertyEntryProps<BestellpositionArtikelLoseWareView>): JSX.Element | null => {
    const dispatch = useDispatch();
    const { setFieldValue, values } = useFormikContext<any>();
    const [zulagenList, setZulagenList] = useState<ZulageEntry[]>([
        {
            zulageId: undefined,
            dosierwert: undefined,
            zulageText: '',
            zulage: false,
        },
    ]);
    const lastIndexRef = useRef<number | undefined>(undefined);
    const initialLoadedRef = useRef(false);
    // Nach Löschen ist Formik kurz noch auf alter zulagenData-Länge; sonst würde der Pull-from-Form-Effekt die Zeile sofort wiederherstellen.
    const skipNextFormikPullRef = useRef(false);

    const { warenempfaengerPartnerNummer, rechnungsempfaengerPartnerNummer } = useArtikelZulagePartnerContext();
    const artikelLoseWareList = useSelector(selectArtikelLoseWareList);
    const artikelStamm = artikelLoseWareList.find((a) => a.nummer === bestellposition.nummer);

    const artikelGruppeIdFromPosition =
        bestellposition.artikelGruppeId != null && String(bestellposition.artikelGruppeId).trim() !== ''
            ? String(bestellposition.artikelGruppeId).trim()
            : '';
    const effectiveArtikelGruppeId = artikelGruppeIdFromPosition || String(artikelStamm?.artikelGruppeId ?? '').trim();

    const werkIdForZulageApi =
        bestellposition.werkId != null && String(bestellposition.werkId).trim() !== '' ? String(bestellposition.werkId).trim() : undefined;

    // Erstelle Filter für diesen spezifischen Artikel (gleiche Logik wie ArtikelZulageMultipleAuswahl)
    const currentFilter = {
        artikelgruppeId: effectiveArtikelGruppeId,
        werkId: werkIdForZulageApi,
        partnerNummer: warenempfaengerPartnerNummer,
        rechnungsempfaengerPartnerNummer: rechnungsempfaengerPartnerNummer || undefined,
    };

    // Rohliste aus dem Store; aktiv !== false (explizit deaktivierte ausblenden, undefined wie API-Default zulassen)
    const zulagenRaw = useSelector((state: any) => ArtikelZulageSelectors.zulagenByFilter(state, currentFilter));
    const zulagen = zulagenRaw.filter((z: ArtikelZulage) => z.aktiv !== false);
    const isLoading = useSelector(ArtikelZulageSelectors.loading);
    const loadFailed = useSelector(ArtikelZulageSelectors.loadFailed);

    // Lade Zulagen für diesen Artikel
    useEffect(() => {
        const filter = createArtikelZulageFilter(effectiveArtikelGruppeId, werkIdForZulageApi, {
            warenempfaengerPartnerNummer,
            rechnungsempfaengerPartnerNummer,
        });
        if (filter) {
            dispatch(getArtikelZulagen(filter));
        }
    }, [dispatch, effectiveArtikelGruppeId, werkIdForZulageApi, warenempfaengerPartnerNummer, rechnungsempfaengerPartnerNummer]);

    // Dropdown-Optionen erstellen
    const zulageOptions: ZulageOption[] = zulagen.map((zulage) => {
        return {
            value: zulage.id,
            label:
                zulage.preis !== undefined && zulage.einheit
                    ? `${zulage.bezeichnung} (+${zulage.preis} ${zulage.einheit})`
                    : zulage.bezeichnung,
            zulage,
        };
    });

    const hasDropdownOptions = Array.isArray(zulageOptions) && zulageOptions.length > 0;

    // API-Liste + bereits gewählte Zulagen der Position (z. B. aus Bestellung), damit neue Zeilen nie ein leeres Menü sehen
    const mergedZulageOptions = useMemo((): ZulageOption[] => {
        const byValue = new Map<string, ZulageOption>();
        for (const o of zulageOptions) {
            byValue.set(o.value, o);
        }
        zulagenList.forEach((z, idx) => {
            if (!z.zulageId && !z.zulageText?.trim()) return;
            const valueKey = z.zulageId || `synthetic-row-${idx}`;
            if (byValue.has(valueKey)) return;
            if (z.zulageId && byValue.has(z.zulageId)) return;
            byValue.set(valueKey, {
                value: z.zulageId || valueKey,
                label: z.zulageText || z.zulageId || '',
                zulage: {
                    id: z.zulageId || '',
                    bezeichnung: z.zulageText || z.zulageId || '',
                    artikelgruppeId: effectiveArtikelGruppeId || String(bestellposition.artikelGruppeId || ''),
                    partnerNummer: String(warenempfaengerPartnerNummer || ''),
                    aktiv: true,
                    dosierbereichMin: 0,
                    dosierbereichMax: 99,
                } satisfies ArtikelZulage,
            });
        });
        return [...byValue.values()];
    }, [zulageOptions, zulagenList, effectiveArtikelGruppeId, bestellposition.artikelGruppeId, warenempfaengerPartnerNummer]);

    const canLoadArtikelZulagen = Boolean(effectiveArtikelGruppeId) && Boolean(warenempfaengerPartnerNummer);

    // Strukturierte Artikelzulage aus Bestellung (code/id) — auch wenn die API-Liste noch leer ist
    const hasStructuredZulageRow = zulagenList.some((z) => !!z.zulageId);
    // Wie ArtikelZulageMultipleAuswahl (Desktop): Dropdown solange Artikelzulagen geladen werden / Fehler;
    // bei leerer API ohne bestehende Codes → Freitext (Sonderherstellung).
    const showDropdownLayout =
        hasDropdownOptions || hasStructuredZulageRow || (canLoadArtikelZulagen && isLoading) || (canLoadArtikelZulagen && loadFailed);

    // Bedingtes Label: echte/Fallback-Artikelzulage vs. reine Sonderherstellung (nur Freitext)
    const displayLabel = showDropdownLayout ? 'Artikelzulage' : 'Zulage (*)';

    // Handler für neue Zulage hinzufügen
    const handleAddZulage = () => {
        if (zulagenList.length >= 4) return;
        if (canLoadArtikelZulagen) {
            const filter = createArtikelZulageFilter(effectiveArtikelGruppeId, werkIdForZulageApi, {
                warenempfaengerPartnerNummer,
                rechnungsempfaengerPartnerNummer,
            });
            if (filter) {
                dispatch(getArtikelZulagen(filter));
            }
        }
        setZulagenList([...zulagenList, { zulageId: undefined, dosierwert: undefined, zulageText: '', zulage: false }]);
    };

    // Handler für Zulage entfernen
    const handleRemoveZulage = (index: number) => {
        skipNextFormikPullRef.current = true;
        const newList = zulagenList.filter((_, i) => i !== index);
        setZulagenList(newList.length > 0 ? newList : [{ zulageId: undefined, dosierwert: undefined, zulageText: '', zulage: false }]);
    };

    // Handler für Dropdown-Auswahl
    const handleZulageSelect = (option: any, index: number) => {
        const zulageOption = option as ZulageOption | null;
        const newList = [...zulagenList];
        const defaultDosierwert = zulageOption ? zulageOption.zulage.dosierbereichMin ?? 0 : undefined;
        newList[index] = {
            ...newList[index],
            zulageId: zulageOption ? zulageOption.value : undefined,
            zulageText: zulageOption ? zulageOption.zulage.bezeichnung : '',
            zulage: !!zulageOption,
            dosierwert: zulageOption ? newList[index].dosierwert ?? defaultDosierwert : undefined,
        };
        setZulagenList(newList);
    };

    // Handler für Dosierwert-Änderung
    const handleDosierwertChange = (value: string, index: number) => {
        const numValue = value ? parseFloat(value) : undefined;
        const newList = [...zulagenList];
        newList[index] = {
            ...newList[index],
            dosierwert: numValue,
        };
        setZulagenList(newList);
    };

    // Handler für Freitext-Änderung
    const handleFreitextChange = (value: string, index: number) => {
        const newList = [...zulagenList];
        newList[index] = {
            ...newList[index],
            zulageText: value,
            zulageId: undefined,
            zulage: value.length > 0,
        };
        setZulagenList(newList);
    };

    // Initialisiere zulagenList aus Formular; beim Zurücknavigieren wiederherstellen, wenn Form mehr Einträge hat
    useEffect(() => {
        if (bestellposition.index === undefined) return;
        if (lastIndexRef.current !== bestellposition.index) {
            lastIndexRef.current = bestellposition.index;
            initialLoadedRef.current = false;
        }

        if (skipNextFormikPullRef.current) {
            skipNextFormikPullRef.current = false;
            return;
        }

        const current = values?.bestellpositionenView?.[bestellposition.index];
        if (!current) return;

        const fromZulagenData = current.zulagenData?.zulagen;
        const formHasMultipleZulagen = Array.isArray(fromZulagenData) && fromZulagenData.length > 0;
        const formHasMoreThanCurrent = formHasMultipleZulagen && fromZulagenData.length > zulagenList.length;

        if (formHasMultipleZulagen && (!initialLoadedRef.current || formHasMoreThanCurrent)) {
            setZulagenList(
                fromZulagenData.map((e) => ({
                    zulageId: e.zulageId,
                    dosierwert: e.dosierwert,
                    zulageText: e.freitext ?? '',
                    zulage: !!(e.zulageId || (e.freitext && e.freitext.trim().length > 0)),
                }))
            );
            initialLoadedRef.current = true;
            return;
        }
        if (!initialLoadedRef.current && (current.zulage || current.zulageText)) {
            setZulagenList([
                {
                    zulageId: current.zulageId,
                    dosierwert: current.dosierwert,
                    zulageText: current.zulageText || '',
                    zulage: current.zulage || false,
                },
            ]);
            initialLoadedRef.current = true;
        }
    }, [values?.bestellpositionenView, bestellposition.index, zulagenList.length]);

    // Alle Zulagen in Formik speichern (zulagenData), damit beim Absenden alle übertragen werden
    useEffect(() => {
        const basePath = `bestellpositionenView[${bestellposition.index}]`;
        const hasAny = zulagenList.length > 0 && zulagenList.some((z) => z.zulage || (z.zulageText && z.zulageText.trim().length > 0));

        setFieldValue(`${basePath}.zulagenData`, {
            zulagen: zulagenList.map((z) => ({
                zulageId: z.zulageId,
                freitext: z.zulageText ?? '',
                dosierwert: z.dosierwert,
            })),
        });
        setFieldValue(`${basePath}.zulage`, hasAny);
        if (zulagenList.length > 0) {
            const first = zulagenList[0];
            setFieldValue(`${basePath}.zulageText`, first.zulageText || '');
            setFieldValue(`${basePath}.zulageId`, first.zulageId);
            setFieldValue(`${basePath}.dosierwert`, first.dosierwert);
        } else {
            setFieldValue(`${basePath}.zulageText`, '');
            setFieldValue(`${basePath}.zulageId`, undefined);
            setFieldValue(`${basePath}.dosierwert`, undefined);
        }
    }, [zulagenList, setFieldValue, bestellposition.index]);

    // Kein react-animate-height: Bei height="auto" wird die Höhe bei zusätzlichen Zulage-Zeilen nicht neu
    // berechnet — der Inhalt läuft aus und überlagert z. B. Wunschtermin (Listen-Ansicht Bestellansicht).
    if (!bestellposition.zulage) {
        return null;
    }

    return (
        <div className={styles.zulageMobileDropdown}>
            {zulagenList.map((zulageEntry, index) => {
                const selectedZulage =
                    mergedZulageOptions.find((z) =>
                        zulageEntry.zulageId ? z.value === zulageEntry.zulageId : z.value === `synthetic-row-${index}`
                    ) || null;
                const currentZulage = selectedZulage?.zulage;

                if (!showDropdownLayout) {
                    return (
                        <div key={index} className={styles.zulageEntry}>
                            <div className={styles.zulageDetailsRow}>
                                <div className={styles.zulageFieldWrapper}>
                                    <label className={styles.zulageLabel}>{displayLabel}</label>
                                    <input
                                        type="text"
                                        className={styles.freitextInput}
                                        value={zulageEntry.zulageText ?? ''}
                                        onChange={(e) => handleFreitextChange(e.target.value, index)}
                                        placeholder="Zulage eingeben"
                                        disabled={disabled}
                                        aria-label="Zulage (Freitext)"
                                    />
                                    {Config.getConfig().isApp() && (
                                        <p className={styles.freitextHint} role="status">
                                            Diese Zulage wird geprüft und ggf. zur Auswahl hinzugefügt.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }
                return (
                    <div key={index} className={styles.zulageEntry}>
                        <div className={styles.zulageDetailsRow}>
                            <div className={styles.zulageFieldWrapper}>
                                <label className={styles.zulageLabel}>{displayLabel}</label>
                                <Select
                                    options={mergedZulageOptions}
                                    value={selectedZulage}
                                    onChange={(option) => handleZulageSelect(option, index)}
                                    placeholder="Zulage auswählen"
                                    isLoading={isLoading}
                                    isDisabled={disabled}
                                    styles={createCustomStyles(false, false, 'left')}
                                    menuShouldScrollIntoView={true}
                                    menuPlacement="auto"
                                    isClearable={false}
                                    noOptionsMessage={() => 'Keine Zulagen verfügbar'}
                                />
                            </div>
                            <div className={styles.dosierungInfoBoxWrapper}>
                                <label className={styles.dosierungLabel}>Dosierung</label>
                                <div className={styles.dosierungInfoBox}>
                                    <span className={styles.dosierungDisplay}>
                                        {zulageEntry.dosierwert !== undefined && zulageEntry.dosierwert !== null
                                            ? zulageEntry.dosierwert
                                            : currentZulage?.dosierbereichMin || 0}
                                    </span>
                                    <span className={styles.dosierungEinheit}>x</span>
                                    <button
                                        type="button"
                                        className={styles.dosierungStepButton}
                                        onClick={() => {
                                            const min = currentZulage?.dosierbereichMin ?? 0;
                                            const value = parseFloat((zulageEntry.dosierwert ?? min).toString());
                                            const newValue = Math.max(min, Math.round((value - 0.1) * 10) / 10);
                                            handleDosierwertChange(newValue.toString(), index);
                                        }}
                                        disabled={disabled}
                                        aria-label="Dosierung verringern"
                                    >
                                        –
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.dosierungStepButton}
                                        onClick={() => {
                                            const min = currentZulage?.dosierbereichMin ?? 0;
                                            const max = currentZulage?.dosierbereichMax ?? 99;
                                            const value = parseFloat((zulageEntry.dosierwert ?? min).toString());
                                            const newValue = Math.min(max, Math.round((value + 0.1) * 10) / 10);
                                            handleDosierwertChange(newValue.toString(), index);
                                        }}
                                        disabled={disabled}
                                        aria-label="Dosierung erhöhen"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            <button
                                type="button"
                                className={styles.iconButton}
                                onClick={() => handleRemoveZulage(index)}
                                disabled={disabled}
                                aria-label="Zulage entfernen"
                            >
                                <img src={TrashIcon} alt="Entfernen" width={20} height={20} />
                            </button>
                        </div>
                    </div>
                );
            })}
            {/* Nur bei Artikelzulage (Dropdown/API), nicht bei Sonderherstellung (Freitext „Zulage (*)“) */}
            {showDropdownLayout ? (
                <div className={styles.addZulageWrapper}>
                    <button
                        type="button"
                        className={styles.addZulageButton}
                        onClick={handleAddZulage}
                        disabled={disabled || zulagenList.length >= 4}
                    >
                        <img src={AddIcon} alt="Hinzufügen" width={20} height={20} style={{ marginRight: 8 }} />
                        Artikelzulage hinzufügen
                    </button>
                </div>
            ) : null}
            {loadFailed && <div className={styles.error}>Fehler beim Laden der Zulagen. Bitte versuchen Sie es noch einmal.</div>}
        </div>
    );
};
